import { useEffect } from "react";
import { CalendarDays, ArrowRight, BadgeCheck, Clock3, UserRound, MessageSquare } from "lucide-react";
import { SERVICES, formatPrice } from "@/lib/business";
import { useBooking } from "@/components/site/BookingDialog";
import { GlowBackdrop } from "@/components/site/GlowBackdrop";

const HIGHLIGHT = "Express-Zulassung";
const PROMISES = [
  [BadgeCheck, "Faire Festpreise"],
  [Clock3, "Am selben Tag erledigt"],
  [UserRound, "Persönlicher Ansprechpartner"],
];

const PriceCard = ({ s, i, onBook }) => {
  const hot = s.name === HIGHLIGHT;
  const ask = s.price == null;
  return (
    <article
      className={`group relative flex items-center gap-3 sm:flex-col sm:items-stretch sm:justify-between rounded-2xl px-4 py-4 sm:p-7 backdrop-blur-md transition-[transform,background-color,border-color] duration-300 hover:-translate-y-1 shadow-[0_24px_48px_-28px_rgba(0,0,0,0.75)] ${
        hot ? "bg-white text-ink border border-white mt-2 sm:mt-0" : "bg-white/[0.08] text-white border border-white/15 hover:bg-white/[0.14] hover:border-white/30"
      }`}
      data-testid={`price-card-${i}`}
    >
      {hot && <span className="lb absolute -top-3 left-4 sm:left-7 rounded-full bg-[#f5b301] text-navy px-3 py-1 shadow-md">Am schnellsten</span>}
      <div className="flex-1 min-w-0">
        <span className={`lb tn ${hot ? "text-inkmuted" : "text-white/55"}`}>{String(i + 1).padStart(2, "0")}</span>
        <h3 className="mt-1 sm:mt-3 text-[15px] sm:text-lg font-semibold leading-snug sm:min-h-[3.5rem]">{s.name}</h3>
      </div>
      <div className="flex items-center sm:items-end justify-between gap-3 sm:gap-4 sm:mt-6 shrink-0">
        <p className={`dp tn leading-none whitespace-nowrap ${ask ? "text-lg sm:text-2xl" : "text-3xl sm:text-5xl"} ${hot ? "text-plate" : "text-white [text-shadow:0_6px_24px_rgba(127,166,207,0.45)]"}`} data-testid={`service-price-${i}`}>
          {formatPrice(s.price)}
        </p>
        <button
          type="button"
          onClick={() => onBook(s.name)}
          aria-label={`${s.name} buchen`}
          className={`flex h-9 w-9 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full transition-colors ${
            hot ? "bg-plate text-white hover:bg-plate-hover" : "bg-white/15 text-white group-hover:bg-white group-hover:text-plate"
          }`}
          data-testid={`price-card-book-${i}`}
        >
          {ask ? <MessageSquare size={18} /> : <ArrowRight size={18} />}
        </button>
      </div>
    </article>
  );
};

export default function Preise() {
  const openBooking = useBooking();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Leistungen & Preise – PC Zulassungsdienst Berlin";
  }, []);

  return (
    <main data-testid="prices-page" className="shade-dark text-paper relative isolate overflow-hidden">
      <GlowBackdrop tone="navy" />
      <section className="pt-10 pb-8 sm:pt-20 sm:pb-14 md:pt-28 md:pb-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 grid md:grid-cols-[220px_1fr] gap-6 sm:gap-12">
          <aside className="lb text-steel border-l-2 border-plate-light pl-4 h-fit">
            <p>Leistungen & Preise</p>
            <p className="mt-2">Festpreise · Berlin</p>
          </aside>
          <div className="min-w-0">
            <h1 className="dp pc-h1 [text-shadow:0_10px_40px_rgba(0,0,0,0.35)]"><span className="ln"><span>Klare Preise.</span></span><span className="ln"><span className="text-plate-light">Keine Überraschung.</span></span></h1>
            <p className="fu mt-4 sm:mt-6 max-w-xl text-base sm:text-lg text-steel">Ein Festpreis pro Leistung, persönlich betreut – und am selben Tag erledigt.</p>
            <ul className="fu fu2 mt-6 sm:mt-8 flex flex-wrap gap-2 sm:gap-3">
              {PROMISES.map(([Icon, text]) => (
                <li key={text} className="flex items-center gap-1.5 sm:gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 sm:px-4 sm:py-2 text-[13px] sm:text-base font-semibold backdrop-blur-md"><Icon size={16} className="text-plate-light" /> {text}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="pb-14 sm:pb-24 md:pb-32">
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <div className="st grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5" data-testid="services-list">
            {SERVICES.map((s, i) => <PriceCard key={s.name} s={s} i={i} onBook={openBooking} />)}
          </div>
          <p className="mt-6 text-sm text-steel">Pfeil anklicken, um die Leistung direkt für Ihren Termin auszuwählen.</p>

          <div className="mt-10 sm:mt-16 grid lg:grid-cols-[1.2fr_1fr] gap-4 sm:gap-6 items-stretch">
            <figure className="relative overflow-hidden rounded-2xl min-h-[200px] sm:min-h-[280px] soft-photo border border-white/10">
              <img src="/img/auto-schwarz.jpg" alt="Schwarzes Auto mit P&C Kennzeichen" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-[center_55%] transition-transform duration-700 hover:scale-105" />
              <figcaption className="lb absolute inset-x-0 bottom-0 text-white bg-gradient-to-t from-black/60 to-transparent p-5 pt-14">Kennzeichen & Wunschkennzeichen</figcaption>
            </figure>
            <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md p-6 sm:p-10 flex flex-col justify-between gap-6 sm:gap-8 soft-photo">
              <div className="absolute -z-10 -top-20 -right-20 h-64 w-64 rounded-full bg-plate/50 blur-[80px]" aria-hidden="true" />
              <div>
                <p className="lb text-white/70">Für Privat, Autohäuser & Fuhrparks</p>
                <h2 className="dp pc-h2 mt-4">Heute anfragen. Heute fahren.</h2>
              </div>
              <div className="flex flex-wrap gap-4">
                <button type="button" onClick={openBooking} className="lb inline-flex items-center gap-2 rounded-full bg-white text-navy px-6 py-4 transition-colors hover:bg-paper2" data-testid="prices-booking-btn">
                  <CalendarDays size={14} /> Termin online buchen
                </button>
                <a href="/#kontakt" className="lb rounded-full border-2 border-white/80 px-6 py-4 transition-colors hover:bg-white hover:text-navy" data-testid="prices-inquiry-btn">Anfrage senden</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
